<template>
  <div>
    <tabs @change="onTabChange">
      <tab title="My Folders">
        <br>
        <div class="form-group">
          <input
            type="text"
            class="form-control"
            id="studentNameFilter"
            placeholder="Filter folders"
            v-model="studentNameFilter"
            aria-label="Filter folders"
            v-if="(userOwnedFolders || []).length > 5">
        </div>
        <h5>Recent folders</h5>
        <ul class="nav nav-pills">
          <li
            v-for="student in filteredRecentStudents"
            :key="student.uuid"
            :class="{active: isSelectedExistingStudent(student)}">
            <button
              :class="['fs-block', 'link-button', (isSelectedExistingStudent(student)) ? 'selected-student' : 'unselected-student']"
              :aria-pressed="isSelectedExistingStudent(student) ? 'true' : 'false'"
              @click="selectStudent(student)">{{ student.name }}</button>
          </li>
        </ul>

        <template v-if="filteredStudentsMinusRecent.length > 0">
          <h5>Other folders</h5>
          <ul class="nav nav-pills">
            <li
              v-for="student in filteredStudentsMinusRecent"
              :key="student.uuid"
              :class="{active: isSelectedExistingStudent(student)}">
              <button
                :class="['fs-block', 'link-button', (isSelectedExistingStudent(student)) ? 'selected-student' : 'unselected-student']"
                :aria-pressed="isSelectedExistingStudent(student) ? 'true' : 'false'"
                @click="selectStudent(student)">{{ student.name }}
              </button>
            </li>
          </ul>
        </template>
      </tab>

      <tab :disabled="disableSharedWithMeTab">
        <template #title>
          <tooltip
            v-if="disableSharedWithMeTab"
            placement="bottom"
            :text="disabledTabTooltipContent"
            target=".shared-tab-title"/>
          <span
            class="material-icons group-icon"
            aria-hidden="true"
            :class="`${disableSharedWithMeTab ? 'disabled-tab' : ''}`"
          >group
          </span>
          <span class="shared-tab-title" :class="`${disableSharedWithMeTab ? 'disabled-tab' : ''}`">Shared with Me</span>
        </template>
        <br>
        <div class="form-group">
          <input
            type="text"
            class="form-control"
            placeholder="Filter folders"
            v-model="studentNameFilter"
            aria-label="Filter folders">
        </div>
        <h5>Folders shared with me</h5>
        <template v-if="(sharedFolders || []).length > 0">
          <ul class="nav nav-pills">
            <li
              v-for="student in filteredSharedFolders"
              :key="student.uuid"
              :class="{active: isSelectedExistingStudent(student)}">
              <popover
                placement="bottom"
                trigger="hover-focus"
                custom-class="popover-as-tooltip avatar-popover"
              >
                <button
                  :class="['fs-block', 'link-button', (isSelectedExistingStudent(student)) ? 'selected-student' : 'unselected-student', 'd-inline-flex', 'align-center']"
                  :aria-pressed="isSelectedExistingStudent(student) ? 'true' : 'false'"
                  @click="selectStudent(student)"
                  :aria-label="sharedFolderAriaLabel(student)"
                >
                  <span class="mx-xxs">{{ student.name }}</span>
                  <UserAvatar
                    :user-info="student.folderMembers && student.folderMembers.getFolderOwner()"
                    :disable-tooltip="true"
                  />
                </button>
                <template slot="popover" v-if='ownerNameDisplay(student)'>
                  <p class="fs-exclude">{{ ownerNameDisplay(student) }}</p>
                  <p v-if='ownerEmailDisplay(student)' class="fs-exclude">{{ ownerEmailDisplay(student) }}</p>
                </template>
              </popover>
            </li>
          </ul>
          <div class="alert alert-info" role="alert">
            <span class="material-icons-round blue-info" aria-hidden="true">info_outline</span>
            <div class="alert-text">
              <span class="heavy-text">Edit access:</span>&nbsp;Once you contribute this to a Shared Folder, only the Owner of the folder will be able to make edits to it.
            </div>
          </div>
        </template>
        <template v-else>
          <p class="alert alert-secondary" role="alert">
            <span class="material-icons-round" aria-hidden="true">info_outline</span>
            You currently do not have any folders shared with you.
          </p>
        </template>
      </tab>

      <tab title="New Folder">
        <h5>Folder Name <StudentPiiTooltip class="gb-blue-medium"/></h5>
        <div class="form-group student-name" :class="{'has-error': !isNewStudent && newStudentName}">
          <input
            :disabled="maxStudentsReached"
            v-model="newStudentName"
            type="text"
            class="form-control fs-block"
            placeholder="New folder"
            aria-label="New folder name"
            @keyup="newStudentNameUpdated">
        </div>
        <alert
          v-if="!isNewStudent && newStudentName"
          type="danger"
          class="alert-wrapper">
          <span class="material-icons-round" aria-hidden="true">warning_amber</span>
          <span>A folder in your library already exists with this name.</span>
        </alert>
        <alert v-if="maxStudentsReached" type="warning">
          <b>Maximum folders reached.</b> {{ maxStudentsErrorMessage }}
        </alert>
      </tab>
    </tabs>

  </div>
</template>

<script>
import { Tabs, Tab } from 'uiv'
import StudentPiiTooltip from '@/components/library/StudentPiiTooltip'
import { libraryQuotasMixin } from '@/components/library/library_quotas_mixin'
import { escapeRegExp } from '@/util/regex'
import { CONTENT_TYPES } from '@/util/access_visibility'
import { mapActions, mapGetters } from 'vuex'
import UserAvatar from '@/components/UserAvatar.vue'

export default {
  name: 'ChooseStudentForm',
  mixins: [libraryQuotasMixin],
  components: {
    UserAvatar,
    Tabs,
    Tab,
    StudentPiiTooltip
  },
  data() {
    return {
      studentNameFilter: '',
      newStudentName: '',
      selectedExistingStudent: null,
    }
  },
  props: {
    recentStudents: {
      type: Array,
      default() {
        return []
      }
    },
    preselectedStudent: {
      type: Object,
      required: false,
      default() {
        return null
      }
    },
    contentType: {
      type: String,
      required: false,
      default: CONTENT_TYPES.GOAL
    }
  },
  computed: {
    ...mapGetters('library', ['userOwnedFolders', 'sharedFolders']),
    ...mapGetters('user', ['userId']),
    activeStudent() {
      if (!this.newStudentName) {
        return this.selectedExistingStudent
      } else {
        return { name: this.newStudentName }
      }
    },
    allStudentNames() {
      return (this.userOwnedFolders || []).map(student => student.name.trim().toLowerCase())
    },
    isNewStudent() {
      return this.newStudentName && this.allStudentNames.indexOf(this.newStudentName.trim().toLowerCase()) < 0
    },
    studentNameRegex() {
      return (this.studentNameFilter && new RegExp(escapeRegExp(this.studentNameFilter), 'i')) || null
    },
    filteredRecentStudents() {
      if (this.studentNameRegex) {
        return this.recentStudents.filter(student => {
          return student.name.match(this.studentNameRegex)
        })
      } else {
        return this.recentStudents
      }
    },
    filteredStudentsMinusRecent() {
      return (this.userOwnedFolders || []).filter(student => {
        let name = student.name
        let notInRecent = this.recentStudents.every(student => {
          return student.name !== name
        })
        if (this.studentNameRegex) {
          return notInRecent && name.match(this.studentNameRegex)
        } else {
          return notInRecent
        }
      })
    },
    filteredSharedFolders() {
      const shared = (this.sharedFolders || [])
      if (this.studentNameRegex) {
        return shared.filter(student => {
          return student.name.match(this.studentNameRegex)
        })
      }
      return shared
    },
    disableSharedWithMeTab() {
      return this.contentType === CONTENT_TYPES.PRESENTLEVEL
    },
    disabledTabTooltipContent() {
      return 'At this time, Present Levels statements can only be saved to folders you own.'
    },
  },
  methods: {
    ...mapActions('library', ['fetchStudents', 'fetchRecentStudents']),
    selectStudent(student) {
      this.selectedExistingStudent = { name: student.name, uuid: student.uuid }
      this.newStudentName = ''
      this.$emit('selectedStudent:existing', this.activeStudent)
    },
    ownerNameDisplay(folder) {
      const owner = folder.folderMembers.getFolderOwner()
      if (!owner || !owner.name) {
        return ''
      }
      return `Owner: ${owner.name}`
    },
    ownerEmailDisplay(folder) {
      const owner = folder.folderMembers.getFolderOwner()
      return owner && owner.email
    },
    sharedFolderAriaLabel(folder) {
      const owner = folder.folderMembers.getFolderOwner()
      return `Folder name: ${folder.name}; Folder Owner: ${owner && owner.name || 'unknown'}, `
    },
    newStudentNameUpdated() {
      if (this.newStudentName && this.selectedExistingStudent) {
        this.selectedExistingStudent = null
      }
      if (this.isNewStudent) {
        this.$emit('selectedStudent:new', this.activeStudent)
      } else {
        this.$emit('selectedStudent:new', null)
      }
    },
    onTabChange(index) {
      // index 0 = My Folders
      // index 1 = Shared with Me
      // index 2 = New Folder
      const isSharedTab = index === 1
      this.$emit('tabChange', { isSharedTab })
      if (index === 2 && this.maxStudentsReached) {
        this.sendMaxStudentsReachedEvent()
      }
    },
    isSelectedExistingStudent(student) {
      return (
        !!this.selectedExistingStudent &&
        !!student &&
        this.selectedExistingStudent.uuid === student.uuid
      )
    },
    async loadStudents() {
      try {
        const isPL = this.contentType === CONTENT_TYPES.PRESENTLEVEL
        const includeShared = isPL ? false : true
        await this.fetchRecentStudents({userId: this.userId, includeShared})
        await this.fetchStudents(this.userId)
      } catch (err) {
          console.error(`Error fetching recent students: ${err}`)
      }
    }
  },
  created() {
    if (this.preselectedStudent) {
      this.selectedExistingStudent = this.preselectedStudent
    }
    if (this.userId) {
      this.loadStudents()
    }
  },
  mounted() {
    this.onTabChange(0)
  },
}
</script>

<style lang="less" scoped>
@import '~@/variables.less';
.form-control:focus {
  border: 2px solid @gb_blue_medium;
  box-shadow: none;
}
.alert {
  color: @gray-dark;
  padding: 4px;
  .material-icons-round {
    font-size: 24px;
  }
}
.alert-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;
}
h5 {
  margin-top: 16px;
}
.student-name {
  position: relative;
  .label {
    position: absolute;
    top: 50%;
    right: 8px;
    margin-top: -7px;
  }
}
.selected-student, .unselected-student {
  padding: 10px 15px !important;
  border-radius: 4px;
  &:hover {
    color: @link-hover-color;
    text-decoration: none;
  }
}

.unselected-student:hover {
  background: #EEEEEE;
}

:deep(.nav-tabs) {
  border-bottom: 4px solid @gb_blue_medium;
  li {
    border-radius: 0px;
    height: 40px;
    margin-bottom: 0px;
  }
  li a, li a:hover {
    border: none;
  }
  li a {
      font-size: 14px;
      color: @gb_blue_medium;
      &:focus-visible {
        outline: 2px solid @gb_blue_medium;
        outline-offset: 1px;
      }
      }
  li a:hover {
    background: #EEEEEE;
    }
  li.active > a {
      background: @gb_blue_medium !important;
      color: white !important;
      border-radius: 4px 4px 0px 0px !important;
      border: none !important;
  }
  .vue-portal-target {
    height: inherit;
  }

}

ul.nav-pills {
  margin-bottom: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  .link-button {
    color: @gb_blue_medium;
    &:hover {
      color: @gb_blue_dark;
    }
    &:focus-visible {
      outline: 2px solid @gb_blue_medium;
    }
  }

  li {
    border: 1px solid transparent;
  }
  li.active button {
    color: white;
    background: @gb_blue_medium;
    &:hover {
      color: white;
      background: @gb_blue_dark
    }
  }
}

.alert-secondary {
  border-radius: 4px;
  background:#E6E6E6;
  display: flex;
  width: 568px;
  height: 36px;
  padding: 4px 8px;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.alert-info {
  display: flex;
  width: 100%;
  padding: 12px;
  align-items: center;
  gap: 8px;
  border-radius: 4px;
  background: #E0F5FF;
  color: #4A4A4A;
  font-size: 14px;
  line-height: 1.4;
  margin-top: 12px;

  .alert-text {
    flex: 1;
    padding-top: 1px;
  }
}

.blue-info {
  color: @gb_blue_medium;
}

.heavy-text {
  font-weight: @font-weight-700;
  display: inline;
}
.group-icon {
  vertical-align:middle;
  margin-right:4px;
}
.disabled-tab {
  color: @gray-light;
}
</style>
